import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ingestMarkdown, fetchAccounts } from '../api';
import type { IngestEvent, IngestResult } from '../types';
import { extractTextFromPdf } from '../utils/pdf';
import { formatCurrency } from '../utils/format';

/**
 * Try to pull a bank account number out of statement text. We only trust
 * matches that sit next to an "account"/"a/c" keyword so we don't mistake a
 * large transaction amount for an account number.
 */
function detectAccountNumber(text: string): string | null {
  const patterns = [
    /account\s*(?:no|number|#)?\s*[:\-]?\s*([0-9][0-9\s-]{5,17})/i,
    /\ba\/c\s*(?:no|number)?\.?\s*[:\-]?\s*([0-9][0-9\s-]{5,17})/i,
    /a\/c\s*[:\-]?\s*([0-9][0-9\s-]{5,17})/i,
  ];
  for (const re of patterns) {
    const m = text.match(re);
    if (m) {
      const cleaned = (m[1] ?? '').replace(/[\s-]/g, '');
      if (/^\d{6,18}$/.test(cleaned)) return cleaned;
    }
  }
  return null;
}

interface Props {
  onSuccess: () => void;
}

type StepState = 'pending' | 'active' | 'done' | 'error';

interface Step {
  key: 'extract' | 'upload' | 'ai' | 'import' | 'done';
  label: string;
  state: StepState;
  detail?: string;
}

const INITIAL_STEPS: Step[] = [
  { key: 'extract', label: 'Read PDF / extract text', state: 'pending' },
  { key: 'upload', label: 'Send to the analysis server', state: 'pending' },
  { key: 'ai', label: 'AI reads the statement', state: 'pending' },
  { key: 'import', label: 'Save to Firefly', state: 'pending' },
  { key: 'done', label: 'Finished', state: 'pending' },
];

const ICONS: Record<StepState, string> = {
  pending: '○',
  active: '⟳',
  done: '✓',
  error: '✕',
};

/**
 * Upload a bank statement PDF (text-layer) or raw .md/.txt.
 * Shows a live, stage-by-stage progress log (extract → send → AI → save →
 * done) and a final result summary, so you always know what happened.
 */
export function FileUpload({ onSuccess }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  const [steps, setSteps] = useState<Step[]>(INITIAL_STEPS);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<IngestResult | null>(null);
  const [note, setNote] = useState<string | null>(null);

  // Password-locked PDF flow
  const [pendingPdf, setPendingPdf] = useState<File | null>(null);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  // Account-number prompt flow (shown after extraction, before upload)
  const [pendingAccountText, setPendingAccountText] = useState<string | null>(null);
  const [pendingAccountName, setPendingAccountName] = useState<string | null>(null);
  const [detectedAccount, setDetectedAccount] = useState<string | null>(null);
  const [accountChoice, setAccountChoice] = useState<'auto' | 'new' | 'none'>('auto');
  const [accountValue, setAccountValue] = useState('');
  const [accountList, setAccountList] = useState<string[]>([]);
  const [accountBusy, setAccountBusy] = useState(false);
  const [accountError, setAccountError] = useState<string | null>(null);

  const patchStep = (key: Step['key'], patch: Partial<Step>) =>
    setSteps((prev) => prev.map((s) => (s.key === key ? { ...s, ...patch } : s)));

  const handleEvent = (ev: IngestEvent) => {
    console.log('[upload] event:', ev);
    switch (ev.stage) {
      case 'prepare':
        patchStep('upload', { state: 'done' });
        patchStep('ai', { state: 'active' });
        break;
      case 'ai':
        patchStep('ai', { state: 'active', detail: `chunk ${ev.chunk}/${ev.total}` });
        break;
      case 'validate':
        patchStep('ai', {
          state: 'done',
          detail: `${ev.parsed} transaction${ev.parsed === 1 ? '' : 's'}${
            Number(ev.duplicates) > 0 ? ` (${ev.duplicates} duplicates removed)` : ''
          }`,
        });
        patchStep('import', { state: 'active', detail: '0' });
        break;
      case 'import':
        patchStep('import', { state: 'active', detail: `${ev.inserted}/${ev.total}` });
        break;
      case 'duplicate':
        setNote(String(ev.note ?? 'duplicate skipped'));
        patchStep('done', { state: 'done', detail: 'already uploaded recently' });
        break;
      case 'fallback':
        setNote(String(ev.note ?? 'used the built-in table reader'));
        break;
      case 'done':
        setResult(ev as unknown as IngestResult);
        patchStep('import', { state: 'done', detail: `${ev.transactions_inserted} saved` });
        patchStep('done', { state: 'done', detail: `${ev.transactions_inserted} transaction${ev.transactions_inserted === 1 ? '' : 's'} added` });
        break;
      case 'smart_dedup':
        setNote(String(ev.note ?? 'some transactions already exist'));
        break;
      default:
        break;
    }
  };

  const reset = () => {
    setSteps(INITIAL_STEPS.map((s) => ({ ...s })));
    setError(null);
    setResult(null);
    setNote(null);
    setFileName(null);
  };

  const handleFile = (file?: File) => {
    if (!file) return;
    reset();
    void extractAndUpload(file);
  };

  /**
   * Extract (optionally with a PDF password) then send the text to /api/ingest
   * while streaming live progress. Locked PDFs return early to open the
   * password modal instead of failing.
   */
  const extractAndUpload = async (file: File, password?: string) => {
    setBusy(true);
    setFileName(file.name);
    patchStep('extract', { state: 'active' });
    try {
      let text: string;
      const isPdf = /\.pdf$/i.test(file.name) || file.type === 'application/pdf';
      if (isPdf) {
        console.log(
          `[upload] extracting text from PDF: "${file.name}" (${file.size} bytes)` +
            (password ? ' — with password' : ''),
        );
        const result = await extractTextFromPdf(file, password);
        if (result.status !== 'ok') {
          // Locked PDF → (re)open the password prompt.
          patchStep('extract', { state: result.status === 'incorrect_password' ? 'error' : 'pending' });
          setPendingPdf(file);
          setPasswordInput('');
          setPasswordError(
            result.status === 'incorrect_password' ? 'Incorrect password — please try again.' : null,
          );
          setBusy(false);
          return;
        }
        text = result.text;
      } else {
        console.log(`[upload] reading text file: "${file.name}"`);
        text = await file.text();
      }

      if (!text.trim()) {
        throw new Error(
          'No text could be extracted from this file. Is the PDF scanned (image-only)?',
        );
      }

      patchStep('extract', { state: 'done', detail: `${text.length.toLocaleString()} chars` });

      // Pause and let the user confirm/choose the bank account number before
      // we tag + import. Detect a candidate from the text and fetch known ones.
      const detected = detectAccountNumber(text);
      let known: string[] = [];
      try {
        const accRes = await fetchAccounts();
        known = accRes.accounts.map((a) => a.number);
      } catch {
        known = [];
      }
      setPendingAccountText(text);
      setPendingAccountName(file.name);
      setDetectedAccount(detected);
      setAccountList(known);
      setAccountChoice(detected ? 'auto' : 'none');
      setAccountValue('');
      setAccountError(null);
      setBusy(false);
      return;
    } catch (e) {
      console.error('[upload] failed:', e);
      const msg = e instanceof Error ? e.message : 'Upload failed';
      setError(msg);
      setSteps((prev) =>
        prev.map((s) => (s.state === 'active' ? { ...s, state: 'error' as const } : s)),
      );
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const submitPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pendingPdf) return;
    const file = pendingPdf;
    setPendingPdf(null); // close modal; reopen on failure if needed
    setPasswordBusy(true);
    setPasswordError(null);
    try {
      await extractAndUpload(file, passwordInput);
    } finally {
      setPasswordBusy(false);
    }
  };

  const cancelAccount = () => {
    setPendingAccountText(null);
    setPendingAccountName(null);
    setDetectedAccount(null);
    setAccountError(null);
    if (!result) reset();
  };

  /**
   * Finish the upload with the chosen account number (or none). Tags every
   * imported transaction with `acct:<number>` so accounts can be filtered/sorted
   * later. "New" is validated and checked against already-known account numbers.
   */
  const confirmAccount = async () => {
    if (!pendingAccountText || !pendingAccountName) return;
    const text = pendingAccountText;
    const name = pendingAccountName;

    let acct: string | null = null;
    if (accountChoice === 'auto' && detectedAccount) acct = detectedAccount;
    else if (accountChoice === 'new') acct = accountValue.trim().replace(/[\s-]/g, '');

    setAccountError(null);
    if (accountChoice === 'new') {
      if (!/^\d{6,18}$/.test(acct ?? '')) {
        setAccountError('Enter a valid account number (6–18 digits).');
        return;
      }
      if (accountList.includes(acct ?? '')) {
        setAccountError('That account number already exists — pick “Use detected account”.');
        return;
      }
    }

    setAccountBusy(true);
    setBusy(true);
    patchStep('upload', { state: 'active' });
    try {
      const res = await ingestMarkdown(text, name, handleEvent, acct);
      console.log(`[upload] result: parsed=${res.transactions_parsed} inserted=${res.transactions_inserted} provider=${res.provider} account=${res.account ?? '(none)'}`);
      onSuccess();
    } catch (e) {
      console.error('[upload] failed:', e);
      const msg = e instanceof Error ? e.message : 'Upload failed';
      setError(msg);
      setSteps((prev) =>
        prev.map((s) => (s.state === 'active' ? { ...s, state: 'error' as const } : s)),
      );
    } finally {
      setAccountBusy(false);
      setBusy(false);
      setPendingAccountText(null);
      setPendingAccountName(null);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const cancelPassword = () => {
    setPendingPdf(null);
    setPasswordError(null);
    setPasswordInput('');
    if (!result) reset();
  };

  return (
    <div className="flex flex-col items-end gap-2">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="rounded-xl border border-zinc-700 bg-zinc-800 px-4 py-2 text-sm font-semibold text-zinc-200 transition hover:bg-zinc-700 disabled:opacity-50"
        >
          {busy ? 'Processing…' : '⬆️ Upload PDF'}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.md,text/markdown,text/plain"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
        {result && (
          <button
            type="button"
            onClick={reset}
            className="text-xs font-semibold text-slate-500 transition hover:text-slate-300"
          >
            Clear
          </button>
        )}
      </div>

      {/* Live progress + result panel — floating, dismissible, lower-right */}
      {(busy || error || result || note) && fileName && (
        <div className="fixed bottom-4 right-4 z-50 w-80 max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-zinc-700 bg-zinc-900/95 p-3 text-left shadow-2xl">
          <div className="mb-2 flex items-start justify-between gap-2">
            <p className="truncate text-xs font-semibold text-slate-300" title={fileName}>
              📄 {fileName}
            </p>
            <button
              type="button"
              onClick={reset}
              title="Dismiss"
              className="shrink-0 rounded-lg px-1.5 text-zinc-500 transition hover:bg-zinc-800 hover:text-zinc-200"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
          <ul className="space-y-1">
            {steps.map((s) => (
              <li
                key={s.key}
                className={`flex items-start gap-2 text-xs ${
                  s.state === 'done'
                    ? 'text-emerald-400'
                    : s.state === 'error'
                      ? 'text-rose-400'
                      : s.state === 'active'
                        ? 'text-zinc-300'
                        : 'text-slate-500'
                }`}
              >
                <span className="w-4 shrink-0 text-center">{ICONS[s.state]}</span>
                <span>
                  {s.label}
                  {s.detail ? <span className="text-slate-400"> — {s.detail}</span> : null}
                </span>
              </li>
            ))}
          </ul>

          {note && (
            <p className="mt-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-2 py-1.5 text-xs text-amber-300">
              ℹ️ {note}
            </p>
          )}

          {error && (
            <p className="mt-2 rounded-lg border border-rose-500/30 bg-rose-500/10 px-2 py-1.5 text-xs text-rose-300">
              ⚠️ {error}
            </p>
          )}

          {result && (
            <div className="mt-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-2">
              <p className="text-xs font-semibold text-emerald-300">
                ✅ {result.transactions_inserted} transaction
                {result.transactions_inserted === 1 ? '' : 's'} added
                {result.transactions_parsed > result.transactions_inserted
                  ? ` (${result.transactions_parsed - result.transactions_inserted} duplicate${result.transactions_parsed - result.transactions_inserted === 1 ? '' : 's'} skipped)`
                  : ''}
              </p>
              {result.cross_doc_duplicates && result.cross_doc_duplicates > 0 && (
                <p className="mt-1 text-xs text-amber-300">
                  ⚠️ {result.cross_doc_duplicates} transaction{result.cross_doc_duplicates === 1 ? '' : 's'} already existed in your records (smart dedup)
                </p>
              )}
              <p className="mt-1 text-xs text-slate-300">
                Spent <span className="font-semibold text-rose-300">{formatCurrency(result.total_spent)}</span>
                {' · '}Income <span className="font-semibold text-emerald-300">{formatCurrency(result.total_income)}</span>
              </p>
              {result.insights.spending_traps.length > 0 && (
                <p className="mt-1 text-xs text-slate-400">
                  🎯 Watch: {result.insights.spending_traps.join(', ')}
                </p>
              )}
              {result.parsed_with_fallback && (
                <p className="mt-1 text-xs text-amber-300">
                  ⚙️ AI labels were fuzzy — used the tolerant parser. Review categories if needed.
                </p>
              )}
              <p className="mt-1 text-xs text-slate-500">Parsed by: {result.provider}</p>
            </div>
          )}
        </div>
      )}

      {/* Account-number prompt modal — bottom sheet on mobile, centered on desktop */}
      {pendingAccountText && createPortal(
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-0 sm:items-center sm:p-4">
          <div className="max-h-[90vh] w-full overflow-y-auto rounded-t-2xl border border-zinc-700 bg-zinc-900 p-5 shadow-2xl sm:max-w-sm sm:rounded-2xl">
            <h3 className="text-sm font-semibold text-slate-100">🏦 Which bank account is this?</h3>
            <p className="mt-1 text-xs text-slate-400">
              We tag every transaction with this account number so you can filter & sort by account.
            </p>

            {detectedAccount ? (
              <label className="mt-3 flex items-start gap-2 rounded-lg border border-zinc-700 bg-zinc-800/60 px-3 py-2 text-xs text-zinc-200">
                <input
                  type="radio"
                  name="acct"
                  checked={accountChoice === 'auto'}
                  onChange={() => setAccountChoice('auto')}
                  className="mt-0.5 accent-emerald-500"
                />
                <span>
                  Use detected account{' '}
                  <span className="font-semibold text-emerald-300">{detectedAccount}</span>
                </span>
              </label>
            ) : (
              <p className="mt-3 text-xs text-amber-300">
                No account number was detected in this PDF. Enter it manually or skip.
              </p>
            )}

            <label className="mt-2 flex items-start gap-2 rounded-lg border border-zinc-700 bg-zinc-800/60 px-3 py-2 text-xs text-zinc-200">
              <input
                type="radio"
                name="acct"
                checked={accountChoice === 'new'}
                onChange={() => setAccountChoice('new')}
                className="mt-0.5 accent-emerald-500"
              />
              <span className="flex-1">
                New account number
                <input
                  value={accountValue}
                  onChange={(e) => setAccountValue(e.target.value)}
                  onFocus={() => setAccountChoice('new')}
                  placeholder="Enter account number"
                  className="mt-1 w-full rounded-lg border border-slate-700 bg-zinc-800 px-2.5 py-1.5 text-sm text-slate-100 placeholder-slate-500 outline-none focus:border-zinc-500"
                />
              </span>
            </label>

            {accountList.length > 0 && (
              <div className="mt-2">
                <p className="text-[10px] uppercase tracking-wider text-zinc-600">Already in your data</p>
                <div className="mt-1 flex flex-wrap gap-1">
                  {accountList.map((a) => (
                    <span key={a} className="rounded-md border border-zinc-800 bg-zinc-950/60 px-1.5 py-0.5 text-[10px] text-zinc-400">
                      {a}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <label className="mt-2 flex items-start gap-2 rounded-lg border border-zinc-700 bg-zinc-800/60 px-3 py-2 text-xs text-zinc-500">
              <input
                type="radio"
                name="acct"
                checked={accountChoice === 'none'}
                onChange={() => setAccountChoice('none')}
                className="mt-0.5 accent-emerald-500"
              />
              <span>Skip — don't tag an account</span>
            </label>

            {accountError && <p className="mt-2 text-xs text-rose-400">{accountError}</p>}

            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={cancelAccount}
                className="rounded-lg px-3 py-1.5 text-xs font-semibold text-slate-400 hover:text-slate-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void confirmAccount()}
                disabled={accountBusy || (accountChoice === 'new' && !accountValue.trim())}
                className="rounded-lg bg-emerald-500 px-4 py-1.5 text-xs font-semibold text-black transition hover:bg-emerald-400 disabled:opacity-50"
              >
                {accountBusy ? 'Importing…' : 'Import'}
              </button>
            </div>
          </div>
        </div>
      , document.body)}

      {/* Password-locked PDF modal — floating, lower-right, dismissible */}
      {pendingPdf && createPortal(
        <div className="fixed bottom-4 right-4 z-50 w-full max-w-sm">
          <form
            onSubmit={submitPassword}
            className="rounded-2xl border border-zinc-700 bg-zinc-900 p-5 shadow-2xl"
          >
            <h3 className="text-sm font-semibold text-slate-100">🔒 PDF is password protected</h3>
            <p className="mt-1 text-xs text-slate-400">
              Enter the password for <span className="font-medium text-slate-300">{pendingPdf.name}</span>{' '}
              to unlock it for processing.
            </p>
            <input
              autoFocus
              type="password"
              autoComplete="off"
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
              placeholder="PDF password"
              className="mt-3 w-full rounded-lg border border-slate-700 bg-zinc-800 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 outline-none focus:border-zinc-500"
            />
            {passwordError && <p className="mt-2 text-xs text-rose-400">{passwordError}</p>}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={cancelPassword}
                className="rounded-lg px-3 py-1.5 text-xs font-semibold text-slate-400 hover:text-slate-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={passwordBusy || !passwordInput}
                className="rounded-lg bg-zinc-700 px-4 py-1.5 text-xs font-semibold text-zinc-100 transition hover:bg-zinc-600 disabled:opacity-50"
              >
                {passwordBusy ? 'Unlocking…' : 'Unlock'}
              </button>
            </div>
          </form>
        </div>
      , document.body)}
    </div>
  );
}
